/**
 * @file AiConfigurationCard.tsx
 * @brief Cascading provider→model pickers for the AI pipeline switchboard.
 *
 * @details
 * Flush GridBlock sections (29-07-2026): Conversation / Meetings & notes /
 * Live realtime. Writes to `system_settings` via PUT /api/admin/settings.
 * Providers from GET /api/admin/ai/catalog with static fallback while loading.
 *
 * Meetings & notes (13-10-2026, mini switch): one picker per meeting step
 * instead of Short/Long meetings, which silently drove the report from 45
 * minutes, the fact check, the second look and Meeting Notes at once.
 * "Model routing" is the one-change rollback: "By meeting length" puts every
 * step back on the two shared models (BACKEND meetingRouting.mjs).
 *
 * @author DES
 * @date 11-07-2026
 * @updated 29-07-2026 — GridBlock flush layout
 * @updated 13-10-2026 — per-step meeting models, routing, agenda second look
 * @updated 15-10-2026 — tabs instead of two long columns (DES: "is section ko
 *   bhi tabs main karoo they are too long"): Interpreter (Conversation + Live
 *   realtime) · Meetings & notes · Speech to text. Hidden tabs stay mounted.
 */

import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Bot, ChevronDown, KeyRound, Languages, LoaderCircle, Mic, Save, Users } from "lucide-react";
import { FormSheetTabs, type FormSheetTab } from "@/components/ui/FormSheetTabs";
import { Button } from "@/components/ui/Button";
import { FormFieldGroup } from "@/components/ui/FormDialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { Input } from "@/components/ui/Input";
import { Skeleton } from "@/components/ui/Skeleton";
import { Switch } from "@/components/ui/Switch";
import {
  GridBand,
  GridCell,
} from "@/features/analytics/components/GridBlock";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import {
  useSystemSettings,
  useUpdateSettings,
  type UpdateSettingsInput,
} from "@/features/billing/api";
import { useAiCatalog, type AiCatalog } from "../api";
import {
  FALLBACK_AI_CATALOG,
  MT_MODEL_OPTIONS,
  MT_PROVIDER_OPTIONS,
  MT_SONIOX_HINT,
  REALTIME_PROVIDER_OPTIONS,
} from "../schemas";
import { NOTE_REPORT_FOCUSED, RETIRED_MODELS, RETIRED_NOTE, URDU_LOANWORDS, VOICE_RESEG, isRetired, type Switch as SettingSwitch } from "./aiPipeline.ts";

type Stage = "TTS" | "ASR" | "LLM";

const modelKey = (stage: Stage, provider: string) =>
  `${provider.toUpperCase()}_MODEL_${stage}`;

type LlmTaskField = { task: string; label: string; hint: string };

/** One picker per meeting step (BACKEND meetingRouting.mjs, 13-10-2026).
 *  Used while MEETING_LLM_ROUTING is "per_step". */
const MEETING_STEP_TASKS: { before: LlmTaskField[]; after: LlmTaskField[] } = {
  before: [
    {
      task: "MEETING_REPORT",
      label: "Meeting report",
      hint: "Writes the Meeting Intelligence report (summary, decisions, to-dos, mind map) for meetings shorter than the long-meeting length below.",
    },
    {
      task: "MEETING_REPORT_LONG",
      label: "Meeting report, long meetings",
      hint: "Writes the report for meetings of the long-meeting length or longer, where more is agreed and easier to miss.",
    },
    {
      task: "VERIFY_JUDGE",
      label: "Fact check",
      hint: "Checks each decision and to-do against the transcript and removes what was not said.",
    },
    {
      task: "RESCUE",
      label: "Second look",
      hint: "Searches the whole transcript again for an item the fact check could not find, before it is removed.",
    },
  ],
  // Agenda second look (on/off) sits between the two lists.
  after: [
    {
      task: "NOTES_V2",
      label: "Meeting Notes",
      hint: "Writes the sectioned notes and next steps for a Meeting Notes recording.",
    },
    {
      task: "URDU_SCRIPT",
      label: "Urdu script fix",
      hint: "Turns Urdu that was heard as Hindi script into Urdu script, in meetings, notes and interpretation.",
    },
  ],
};

const PERSONAL_NOTES_TASK: LlmTaskField = {
  task: "NOTES",
  label: "Personal notes",
  hint: "Summarises personal voice notes into points and tasks. Also runs the medical report extractors.",
};

/** Ask's planner (ADR-050, 21-10-2026): its own task, whatever the routing. */
const ASK_PLANNER_TASK: LlmTaskField = {
  task: "ASK_PLANNER",
  label: "Ask AI planner",
  hint: "Reads each Ask AI question first and picks the meetings, dates and people to search. Small helpers then writes the answer.",
};

/** The two models other steps share. What they drive depends on the routing. */
const sharedTasks = (byLength: boolean): LlmTaskField[] => [
  {
    task: "BOARDROOM_SHORT",
    label: "Small helpers",
    hint: byLength
      ? "Meeting report under 45 minutes and the Urdu script fix, plus agenda reading, clean-up, bookmark polish, Ask AI, corrections and speaker moves."
      : "Agenda reading, report clean-up, bookmark polish, Ask AI, corrections, speaker moves and the My notes × meeting report.",
  },
  {
    task: "BOARDROOM_LONG",
    label: "Deep checks",
    hint: byLength
      ? "Meeting report from 45 minutes, fact check, second look and Meeting Notes."
      : "Checks the My notes × meeting report against the transcript.",
  },
];

const ROUTING_OPTIONS = [
  { value: "per_step", label: "One model per step (from 13-10-2026)" },
  { value: "length_tier", label: "By meeting length (before 13-10-2026)" },
] as const;

const llmTaskKey = (provider: string, task: string) =>
  `${provider.toUpperCase()}_MODEL_${task}`;

export const DEFAULTS: Record<string, string> = {
  ASR_PROVIDER: "auto",
  MT_PROVIDER: "openai",
  TTS_PROVIDER: "auto",
  LLM_PROVIDER: "openai",
  REALTIME_PROVIDER: "openai",
  OPENAI_MODEL_MT: "gpt-4o-mini",
  OPENAI_MODEL_TTS: "gpt-4o-mini-tts-2025-12-15",
  DEEPGRAM_MODEL_TTS: "aura-asteria-en",
  GOOGLE_MODEL_TTS: "chirp3-hd",
  AZURE_MODEL_TTS: "neural",
  // Interpreter Urdu voices when Azure speaks (16-10-2026); rows are created on first save.
  AZURE_VOICE_UR_MALE: "ur-PK-AsadNeural",
  AZURE_VOICE_UR_FEMALE: "ur-PK-UzmaNeural",
  // Same for every Urdu-capable voice provider (today's defaults; one pair each).
  GOOGLE_VOICE_UR_MALE: "ur-IN-Chirp3-HD-Charon",
  GOOGLE_VOICE_UR_FEMALE: "ur-IN-Chirp3-HD-Aoede",
  OPENAI_VOICE_UR_MALE: "ballad",
  OPENAI_VOICE_UR_FEMALE: "nova",
  SONIOX_VOICE_UR_MALE: "Arjun",
  SONIOX_VOICE_UR_FEMALE: "Priya",
  OPENAI_MODEL_ASR: "gpt-4o-mini-transcribe",
  DEEPGRAM_MODEL_ASR: "nova-3",
  OPENAI_MODEL_BOARDROOM_SHORT: "gpt-4.1-mini",
  OPENAI_MODEL_BOARDROOM_LONG: "gpt-4.1",
  OPENAI_MODEL_NOTES: "gpt-4o-mini",
  ANTHROPIC_MODEL_BOARDROOM_SHORT: "claude-haiku-4-5-20251001",
  ANTHROPIC_MODEL_BOARDROOM_LONG: "claude-sonnet-5",
  ANTHROPIC_MODEL_NOTES: "claude-haiku-4-5-20251001",
  GOOGLE_MODEL_BOARDROOM_SHORT: "gemini-2.5-flash",
  GOOGLE_MODEL_BOARDROOM_LONG: "gemini-2.5-pro",
  GOOGLE_MODEL_NOTES: "gemini-2.5-flash",
  // Per-step meeting models (13-10-2026), same seeds as the backend migration.
  MEETING_LLM_ROUTING: "per_step",
  RESCUE_AGENDA_ENABLED: "false",
  // Long-meeting report (13-10-2026): its own model from this many minutes.
  MEETING_REPORT_LONG_MIN: "90",
  OPENAI_MODEL_MEETING_REPORT_LONG: "gpt-4.1",
  ANTHROPIC_MODEL_MEETING_REPORT_LONG: "claude-sonnet-5",
  GOOGLE_MODEL_MEETING_REPORT_LONG: "gemini-2.5-pro",
  OPENAI_MODEL_MEETING_REPORT: "gpt-4.1-mini",
  OPENAI_MODEL_VERIFY_JUDGE: "gpt-4.1",
  OPENAI_MODEL_RESCUE: "gpt-4.1-mini",
  OPENAI_MODEL_NOTES_V2: "gpt-4.1-mini",
  OPENAI_MODEL_URDU_SCRIPT: "gpt-4.1-mini",
  ANTHROPIC_MODEL_MEETING_REPORT: "claude-haiku-4-5-20251001",
  ANTHROPIC_MODEL_VERIFY_JUDGE: "claude-sonnet-5",
  ANTHROPIC_MODEL_RESCUE: "claude-haiku-4-5-20251001",
  ANTHROPIC_MODEL_NOTES_V2: "claude-haiku-4-5-20251001",
  ANTHROPIC_MODEL_URDU_SCRIPT: "claude-haiku-4-5-20251001",
  GOOGLE_MODEL_MEETING_REPORT: "gemini-2.5-flash",
  GOOGLE_MODEL_VERIFY_JUDGE: "gemini-2.5-pro",
  GOOGLE_MODEL_RESCUE: "gemini-2.5-flash",
  GOOGLE_MODEL_NOTES_V2: "gemini-2.5-flash",
  GOOGLE_MODEL_URDU_SCRIPT: "gemini-2.5-flash",
  // Ask planner (ADR-050, 21-10-2026), same seeds as the backend migration.
  OPENAI_MODEL_ASK_PLANNER: "gpt-4.1-mini",
  ANTHROPIC_MODEL_ASK_PLANNER: "claude-haiku-4-5-20251001",
  GOOGLE_MODEL_ASK_PLANNER: "gemini-2.5-flash",
  // Meeting speech-to-text fallback chain (14-10-2026), same defaults as the
  // backend (diarizeBatch.mjs ASR_CHAIN_SETTINGS). Off = Deepgram only.
  ASR_CHAIN_ENABLED: "false",
  ASR_CHAIN_ENGLISH: "soniox,assemblyai,deepgram",
  ASR_CHAIN_URDU: "deepgram,soniox,assemblyai",
  ASR_CHAIN_OTHER: "soniox,deepgram,assemblyai",
  ASR_TIMEOUT_SONIOX_SECS: "1200",
  ASR_TIMEOUT_ASSEMBLYAI_SECS: "1200",
  ASR_TIMEOUT_DEEPGRAM_SECS: "600",
  ASR_MIN_WORDS_PER_MIN: "10",
  ASR_LID_SECONDS: "120",
  // B16 / B18 switches (24-10-2026, W16), seeded off by the backend (00177).
  ASR_URDU_LOANWORDS_LATIN: "false",
  ASR_SONIOX_VOICE_RESEG: "false",
  NOTE_REPORT_FOCUSED_INPUT: "false",
};

/** A saved retired model (W15), shown disabled so the picker says what is saved. */
function retiredItem(value: string | undefined) {
  if (!isRetired(value)) return null;
  return (
    <SelectItem value={value!} disabled>
      {RETIRED_MODELS[value!]}: {RETIRED_NOTE}
    </SelectItem>
  );
}

/** Speech-to-text vendors the meeting chain can use, with their labels. */
const ASR_CHAIN_VENDORS: { value: string; label: string }[] = [
  { value: "soniox", label: "Soniox" },
  { value: "assemblyai", label: "AssemblyAI (EU)" },
  { value: "deepgram", label: "Deepgram" },
];

/** Every order of the three vendors, as "a,b,c" with a readable label. */
export const ASR_ORDER_OPTIONS: { value: string; label: string }[] = (() => {
  const out: { value: string; label: string }[] = [];
  const names = ASR_CHAIN_VENDORS.map((v) => v.value);
  const labelOf = (v: string) => ASR_CHAIN_VENDORS.find((x) => x.value === v)?.label ?? v;
  for (const a of names)
    for (const b of names)
      for (const c of names)
        if (a !== b && b !== c && a !== c)
          out.push({ value: `${a},${b},${c}`, label: [a, b, c].map(labelOf).join(" → ") });
  return out;
})();

export const ASR_CHAIN_GROUPS: { key: string; label: string; hint: string }[] = [
  { key: "ASR_CHAIN_ENGLISH", label: "English meetings", hint: "Picked by a 2-minute language check of the recording." },
  { key: "ASR_CHAIN_URDU", label: "Urdu / Urdu-English meetings", hint: "Deepgram first keeps today's Urdu path." },
  { key: "ASR_CHAIN_OTHER", label: "Other languages", hint: "Deepgram is skipped for a language it does not have." },
];

const ASR_TIMEOUT_FIELDS: { key: string; label: string }[] = [
  { key: "ASR_TIMEOUT_SONIOX_SECS", label: "Soniox timeout (seconds)" },
  { key: "ASR_TIMEOUT_ASSEMBLYAI_SECS", label: "AssemblyAI timeout (seconds)" },
  { key: "ASR_TIMEOUT_DEEPGRAM_SECS", label: "Deepgram timeout (seconds)" },
];

const SETTING_KEYS = Object.keys(DEFAULTS);

const AUTO_CHAIN: Record<"TTS" | "ASR", string[]> = {
  TTS: ["deepgram", "openai"],
  ASR: ["deepgram", "openai"],
};

function settingValue(
  settings: Record<string, { value: string }> | undefined,
  key: string,
): string {
  return settings?.[key]?.value || DEFAULTS[key] || "";
}

type ConfigTab = "interpreter" | "meetings" | "speech";
const CONFIG_TABS: FormSheetTab<ConfigTab>[] = [
  { id: "meetings", label: "Meetings & notes", icon: Users },
  { id: "speech", label: "Speech to text", icon: Mic },
  { id: "interpreter", label: "Interpreter", icon: Languages },
];

function SectionIntro({
  title,
  description,
}: {
  title: string;
  description?: string;
}) {
  return (
    // Section headings a shade under the card heading and over the fields
    // (DES 01-10-2026: "sections and heading ka bg thora different karo").
    <GridBand className="bg-black/[0.03] py-2.5 dark:bg-white/[0.035]">
      <h3 className="text-sm font-semibold tracking-tight">{title}</h3>
      {description ? (
        <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
          {description}
        </p>
      ) : null}
    </GridBand>
  );
}

function Field({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string;
  htmlFor?: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <FormFieldGroup label={label} htmlFor={htmlFor} hint={hint}>
      {children}
    </FormFieldGroup>
  );
}

export function AiConfigurationCard() {
  const { data, isLoading, isError } = useSystemSettings();
  const catalogQuery = useAiCatalog();
  const catalog: AiCatalog = catalogQuery.data ?? FALLBACK_AI_CATALOG;
  const update = useUpdateSettings();

  const [values, setValues] = useState<Record<string, string>>({ ...DEFAULTS });
  const [tuneTts, setTuneTts] = useState(false);
  const [tab, setTab] = useState<ConfigTab>("meetings");
  const panel = (id: ConfigTab) => (tab === id ? "min-w-0" : "hidden");
  const [tuneAsr, setTuneAsr] = useState(false);

  const setVal = (key: string, next: string) =>
    setValues((prev) => ({ ...prev, [key]: next }));

  useEffect(() => {
    if (!data?.settings) return;
    setValues(
      Object.fromEntries(
        SETTING_KEYS.map((k) => [k, settingValue(data.settings, k)]),
      ),
    );
  }, [data]);

  useEffect(() => {
    if (values.TTS_PROVIDER === "auto") {
      /* keep user's disclosure state */
    } else {
      setTuneTts(false);
    }
    if (values.ASR_PROVIDER === "auto") {
      /* keep */
    } else {
      setTuneAsr(false);
    }
  }, [values.TTS_PROVIDER, values.ASR_PROVIDER]);

  const dirtyPayload = useMemo<UpdateSettingsInput | null>(() => {
    if (!data?.settings) return null;
    const patch: Record<string, string> = {};
    for (const key of SETTING_KEYS) {
      const prev = settingValue(data.settings, key);
      if (values[key] !== prev) patch[key] = values[key];
    }
    return Object.keys(patch).length > 0 ? { settings: patch } : null;
  }, [data, values]);

  const handleSave = async () => {
    if (!dirtyPayload) return;
    try {
      await update.mutateAsync(dirtyPayload);
      toast({
        variant: "success",
        title: "AI configuration saved",
        description: `Updated ${Object.keys(dirtyPayload.settings).length} setting(s).`,
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Save failed",
        description:
          err instanceof ApiClientError ? err.message : "Please try again.",
      });
    }
  };

  const renderProviderSelect = (
    stage: Stage,
    settingKey: string,
    id: string,
    includeAuto = true,
  ) => (
    <Select value={values[settingKey]} onValueChange={(v) => setVal(settingKey, v)}>
      <SelectTrigger id={id}>
        <SelectValue placeholder="Select provider" />
      </SelectTrigger>
      <SelectContent>
        {includeAuto && (
          <SelectItem value="auto">Auto — best available</SelectItem>
        )}
        {Object.entries(catalog[stage]).map(([name, entry]) => (
          <SelectItem key={name} value={name} disabled={!entry.configured}>
            {entry.label}
            {!entry.configured ? " — needs API key" : ""}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const renderModelSelect = (stage: Stage, provider: string, label?: string) => {
    const entry = catalog[stage][provider];
    if (!entry) return null;
    const key = modelKey(stage, provider);
    return (
      <Field key={key} label={label ?? "Model"} htmlFor={key.toLowerCase()}>
        <Select value={values[key]} onValueChange={(v) => setVal(key, v)}>
          <SelectTrigger id={key.toLowerCase()}>
            <SelectValue placeholder="Select model" />
          </SelectTrigger>
          <SelectContent>
            {retiredItem(values[key])}
            {entry.models.map((m) => (
              <SelectItem key={m.value} value={m.value}>
                {m.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
    );
  };

  /** An on/off row like Agenda second look; locked while the server has no row (it would save nothing). */
  const renderSwitch = (sw: SettingSwitch, missing: string) => {
    const ready = Boolean(data?.settings?.[sw.key]);
    return (
      <div key={sw.key} className="flex items-center justify-between gap-4 border-b border-border/60 pb-3">
        <div className="min-w-0">
          <p className="text-sm font-medium">{sw.name}</p>
          <p className="text-xs text-muted-foreground">{ready ? sw.line : `${sw.line} ${missing}`}</p>
        </div>
        <Switch
          checked={values[sw.key] === "true"}
          disabled={!ready}
          onCheckedChange={(on) => setVal(sw.key, on ? "true" : "false")}
          aria-label={`Toggle ${sw.name.toLowerCase()}`}
        />
      </div>
    );
  };

  const renderSpeechStage = (
    stage: "TTS" | "ASR",
    providerKey: string,
    providerId: string,
    label: string,
    hint: string,
    tuned: boolean,
    setTuned: (v: boolean) => void,
  ) => {
    const provider = values[providerKey];
    const chain = AUTO_CHAIN[stage]
      .filter((p) => catalog[stage][p]?.configured)
      .map((p) => catalog[stage][p]?.label ?? p);

    return (
      <div className="space-y-3">
        <Field label={label} htmlFor={providerId} hint={hint}>
          {renderProviderSelect(stage, providerKey, providerId)}
        </Field>

        {provider === "auto" ? (
          <div className="border border-border/60 bg-muted/15 px-3 py-2.5">
            <p className="text-xs text-muted-foreground">
              Routes by language: {chain.join(" → ")}.
            </p>
            <button
              type="button"
              className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-foreground/80 hover:text-foreground"
              onClick={() => setTuned(!tuned)}
              aria-expanded={tuned}
            >
              <ChevronDown
                className={`h-3.5 w-3.5 transition-transform ${tuned ? "rotate-180" : ""}`}
              />
              {tuned ? "Hide models" : "Tune models"}
            </button>
            {tuned ? (
              <div className="mt-3 space-y-3 border-t border-border/60 pt-3">
                {AUTO_CHAIN[stage]
                  .filter((p) => catalog[stage][p]?.configured)
                  .map((p) =>
                    renderModelSelect(
                      stage,
                      p,
                      `${catalog[stage][p]?.label ?? p}`,
                    ),
                  )}
              </div>
            ) : null}
          </div>
        ) : (
          renderModelSelect(stage, provider)
        )}
      </div>
    );
  };

  const renderLlmTaskSelect = ({ task, label, hint }: LlmTaskField) => {
    const provider =
      catalog.LLM[values.LLM_PROVIDER] ? values.LLM_PROVIDER : "openai";
    const entry = catalog.LLM[provider];
    if (!entry) return null;
    const key = llmTaskKey(provider, task);
    return (
      <Field key={key} label={label} htmlFor={key.toLowerCase()} hint={hint}>
        <Select value={values[key]} onValueChange={(v) => setVal(key, v)}>
          <SelectTrigger id={key.toLowerCase()}>
            <SelectValue placeholder="Select model" />
          </SelectTrigger>
          <SelectContent>
            {retiredItem(values[key])}
            {entry.models.map((m) => (
              <SelectItem key={m.value} value={m.value}>
                {m.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
    );
  };

  const byLength = values.MEETING_LLM_ROUTING === "length_tier";
  // The chain's rows are seeded when the backend that reads them is deployed;
  // until then saving would update nothing, so the section is read-only.
  const asrChainReady = Boolean(data?.settings?.ASR_CHAIN_ENABLED);
  const asrChainOn = values.ASR_CHAIN_ENABLED === "true";
  // The backend treats anything else as 90; the form refuses to save it.
  const longMinValid = /^[1-9][0-9]*$/.test(values.MEETING_REPORT_LONG_MIN ?? "");
  const agendaRescueOn = values.RESCUE_AGENDA_ENABLED === "true";

  const dormantCount =
    Object.values(catalog.TTS).filter((p) => !p.configured).length +
    Object.values(catalog.LLM).filter((p) => !p.configured).length;

  return (
    <>
      <GridBand className="flex flex-wrap items-center justify-between gap-3 bg-black/[0.06] py-3 dark:bg-white/[0.07]">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            AI
          </p>
          <h3 className="mt-0.5 flex items-center gap-2 text-base font-semibold tracking-tight">
            <Bot className="h-4 w-4 text-primary" />
            AI configuration
          </h3>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Pick providers for conversation, meetings, and notes. Saves apply on
            the next session — no redeploy.
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-3">
          <span className="text-xs text-muted-foreground">
            {dirtyPayload
              ? `${Object.keys(dirtyPayload.settings).length} unsaved change(s)`
              : "All saved"}
          </span>
          <Button
            onClick={handleSave}
            disabled={!dirtyPayload || update.isPending || isLoading || isError || !longMinValid}
          >
            {update.isPending ? (
              <>
                <LoaderCircle className="h-4 w-4 animate-spin" /> Saving…
              </>
            ) : (
              <>
                <Save className="h-4 w-4" /> Save
              </>
            )}
          </Button>
        </div>
      </GridBand>

      {isLoading ? (
        <GridCell>
          <div className="space-y-4">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        </GridCell>
      ) : isError ? (
        <GridCell>
          <p className="text-sm text-destructive">
            Couldn&apos;t load AI settings.
          </p>
        </GridCell>
      ) : (
        <>
          {/* Tabs (DES 15-10-2026: "too long"); were two columns since
              01-10-2026. Hidden panels stay mounted, so nothing is lost. */}
          <div>
            <GridBand className="py-3">
              <FormSheetTabs tabs={CONFIG_TABS} value={tab} onChange={setTab} label="AI configuration sections" compact />
            </GridBand>
            <div className={panel("interpreter")}>
              <SectionIntro
                title="Conversation"
                description="Spoken translation pipeline (turn-by-turn S2S)."
              />
              <GridCell>
                <div className="space-y-4">
                  {renderSpeechStage(
                    "TTS",
                    "TTS_PROVIDER",
                    "tts-provider",
                    "Voice",
                    "TTS — speaks the translated audio the other person hears.",
                    tuneTts,
                    setTuneTts,
                  )}
                  {renderSpeechStage(
                    "ASR",
                    "ASR_PROVIDER",
                    "asr-provider",
                    "Transcription",
                    "ASR — turns spoken audio into text in Conversation. Not Boardroom diarization (that stays on Deepgram).",
                    tuneAsr,
                    setTuneAsr,
                  )}
                  <div className="space-y-3">
                    <Field
                      label="Translation"
                      htmlFor="mt-provider"
                      hint={`MT — translates Conversation transcript text between the two languages. ${MT_SONIOX_HINT}`}
                    >
                      <Select
                        value={values.MT_PROVIDER}
                        onValueChange={(v) => setVal("MT_PROVIDER", v)}
                      >
                        <SelectTrigger id="mt-provider">
                          <SelectValue placeholder="Select provider" />
                        </SelectTrigger>
                        <SelectContent>
                          {MT_PROVIDER_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    {values.MT_PROVIDER === "openai" ||
                    values.MT_PROVIDER === "auto" ||
                    values.MT_PROVIDER === "soniox" ? (
                      <Field
                        label="Translation model"
                        htmlFor="mt-model"
                        hint={
                          values.MT_PROVIDER === "soniox"
                            ? "The GPT model used whenever Soniox did not translate the turn."
                            : "Which GPT model runs the MT step above."
                        }
                      >
                        <Select
                          value={values.OPENAI_MODEL_MT}
                          onValueChange={(v) => setVal("OPENAI_MODEL_MT", v)}
                        >
                          <SelectTrigger id="mt-model">
                            <SelectValue placeholder="Select model" />
                          </SelectTrigger>
                          <SelectContent>
                            {retiredItem(values.OPENAI_MODEL_MT)}
                            {MT_MODEL_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Field>
                    ) : null}
                  </div>
                </div>
              </GridCell>
            </div>
            <div className={panel("meetings")}>
              <SectionIntro
                title="Meetings & notes"
                description="Writing intelligence only, not speech diarization."
              />
              <GridCell>
                {/* Two columns from lg (DES 15-10-2026: "too long"). */}
                <div className="grid gap-x-10 gap-y-4 lg:grid-cols-2">
                  <Field
                    label="Provider"
                    htmlFor="llm-provider"
                    hint="LLM vendor for every step below. Speech to text and speaker diarization are set in Speech to text (meetings) below."
                  >
                    {renderProviderSelect(
                      "LLM",
                      "LLM_PROVIDER",
                      "llm-provider",
                      false,
                    )}
                  </Field>
                  <Field
                    label="Model routing"
                    htmlFor="meeting-llm-routing"
                    hint="By meeting length is the rollback: every step goes back on the two shared models below, and the agenda second look runs."
                  >
                    <Select
                      value={values.MEETING_LLM_ROUTING}
                      onValueChange={(v) => setVal("MEETING_LLM_ROUTING", v)}
                    >
                      <SelectTrigger id="meeting-llm-routing">
                        <SelectValue placeholder="Select routing" />
                      </SelectTrigger>
                      <SelectContent>
                        {ROUTING_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  {byLength ? null : (
                    <div className="contents">
                      {MEETING_STEP_TASKS.before.slice(0, 2).map(renderLlmTaskSelect)}
                      <Field
                        label="Long-meeting length (minutes)"
                        htmlFor="meeting-report-long-min"
                        hint={
                          longMinValid
                            ? "From this length the report uses the long-meeting model. Shorter meetings use Meeting report."
                            : "Enter a whole number of minutes, 1 or more."
                        }
                      >
                        <Input
                          id="meeting-report-long-min"
                          inputMode="numeric"
                          placeholder="90"
                          value={values.MEETING_REPORT_LONG_MIN}
                          onChange={(e) => setVal("MEETING_REPORT_LONG_MIN", e.target.value.replace(/[^0-9]/g, ""))}
                        />
                      </Field>
                      {MEETING_STEP_TASKS.before.slice(2).map(renderLlmTaskSelect)}
                      <div className="flex items-center justify-between gap-4 border-b border-border/60 pb-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium">Agenda second look</p>
                          <p className="text-xs text-muted-foreground">
                            Searches the transcript again for an agenda point the
                            report marked not raised. Uses the Second look model.
                          </p>
                        </div>
                        <Switch
                          checked={agendaRescueOn}
                          onCheckedChange={(on) =>
                            setVal("RESCUE_AGENDA_ENABLED", on ? "true" : "false")
                          }
                          aria-label="Toggle agenda second look"
                        />
                      </div>
                      {MEETING_STEP_TASKS.after.map(renderLlmTaskSelect)}
                    </div>
                  )}
                  {renderLlmTaskSelect(PERSONAL_NOTES_TASK)}
                  {renderLlmTaskSelect(ASK_PLANNER_TASK)}
                  <div className="grid gap-x-10 gap-y-4 border-t border-border/60 pt-4 lg:col-span-2 lg:grid-cols-2">
                    <p className="text-xs font-medium text-muted-foreground lg:col-span-2">
                      {byLength
                        ? "Models by meeting length"
                        : "Shared by the other meeting steps"}
                    </p>
                    {sharedTasks(byLength).map(renderLlmTaskSelect)}
                  </div>
                  <div className="border-t border-border/60 pt-4 lg:col-span-2">
                    {renderSwitch(NOTE_REPORT_FOCUSED, "Arrives with the B18 backend deploy.")}
                  </div>
                </div>
              </GridCell>

            </div>
            <div className={panel("speech")}>
              <SectionIntro
                title="Speech to text (meetings)"
                description="Recorded meetings, gap fill and Personal Notes. Live interpretation is not affected."
              />
              <GridCell>
                <div className="space-y-4">
                  {asrChainReady ? null : (
                    <p className="text-xs text-muted-foreground">
                      Available once the backend update with the fallback chain
                      is deployed. Until then every meeting uses Deepgram.
                    </p>
                  )}
                  <div className="flex items-center justify-between gap-4 border-b border-border/60 pb-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium">Fallback chain</p>
                      <p className="text-xs text-muted-foreground">
                        Off: Deepgram only, as before. On: the vendors below,
                        in order, per meeting language; the next one runs when
                        a vendor fails, times out or returns almost nothing.
                      </p>
                    </div>
                    <Switch
                      checked={asrChainOn}
                      disabled={!asrChainReady}
                      onCheckedChange={(on) => setVal("ASR_CHAIN_ENABLED", on ? "true" : "false")}
                      aria-label="Toggle speech-to-text fallback chain"
                    />
                  </div>
                  {[URDU_LOANWORDS, VOICE_RESEG].map((sw) => renderSwitch(sw, "Arrives with the B16 backend deploy."))}
                  {ASR_CHAIN_GROUPS.map((g) => (
                    <Field key={g.key} label={g.label} htmlFor={`asr-chain-${g.key}`} hint={g.hint}>
                      <Select
                        value={values[g.key]}
                        disabled={!asrChainReady}
                        onValueChange={(v) => setVal(g.key, v)}
                      >
                        <SelectTrigger id={`asr-chain-${g.key}`}>
                          <SelectValue placeholder="Select order" />
                        </SelectTrigger>
                        <SelectContent>
                          {ASR_ORDER_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  ))}
                  {ASR_TIMEOUT_FIELDS.map((t) => (
                    <Field key={t.key} label={t.label} htmlFor={`asr-${t.key}`} hint="After this long the next vendor runs.">
                      <Input
                        id={`asr-${t.key}`}
                        inputMode="numeric"
                        disabled={!asrChainReady}
                        value={values[t.key]}
                        onChange={(e) => setVal(t.key, e.target.value.replace(/[^0-9]/g, ""))}
                      />
                    </Field>
                  ))}
                  <Field
                    label="Near-empty result (words per minute)"
                    htmlFor="asr-min-wpm"
                    hint="A transcript with fewer words a minute counts as failed and the next vendor runs."
                  >
                    <Input
                      id="asr-min-wpm"
                      inputMode="numeric"
                      disabled={!asrChainReady}
                      value={values.ASR_MIN_WORDS_PER_MIN}
                      onChange={(e) => setVal("ASR_MIN_WORDS_PER_MIN", e.target.value.replace(/[^0-9]/g, ""))}
                    />
                  </Field>
                  <Field
                    label="Language check (seconds of audio)"
                    htmlFor="asr-lid-seconds"
                    hint="How much of the start of a meeting the language check listens to."
                  >
                    <Input
                      id="asr-lid-seconds"
                      inputMode="numeric"
                      disabled={!asrChainReady}
                      value={values.ASR_LID_SECONDS}
                      onChange={(e) => setVal("ASR_LID_SECONDS", e.target.value.replace(/[^0-9]/g, ""))}
                    />
                  </Field>
                </div>
              </GridCell>

            </div>
            <div className={panel("interpreter")}>
              <SectionIntro
                title="Live realtime"
                description="Optional low-latency Conversation path."
              />
              <GridCell>
                <Field
                  label="Provider"
                  htmlFor="realtime-provider"
                  hint="Live gpt-realtime-translate path. Choose Disabled to force the standard turn-by-turn pipeline."
                >
                  <Select
                    value={values.REALTIME_PROVIDER}
                    onValueChange={(v) => setVal("REALTIME_PROVIDER", v)}
                  >
                    <SelectTrigger id="realtime-provider">
                      <SelectValue placeholder="Select provider" />
                    </SelectTrigger>
                    <SelectContent>
                      {REALTIME_PROVIDER_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </GridCell>
            </div>
          </div>

          {dormantCount > 0 ? (
            <GridCell>
              <div className="flex items-start gap-2 text-xs text-muted-foreground">
                <KeyRound className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                  {dormantCount} backup provider
                  {dormantCount > 1 ? "s" : ""} waiting on API keys — add keys in
                  the backend env to unlock them here.
                </span>
              </div>
            </GridCell>
          ) : null}
        </>
      )}
    </>
  );
}
